import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find(a => a.id === Number(id));

  if (!article) {
    return (
      <main className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground-800 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">该文章可能已被移除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const categoryInfo = categories.find(c => c.slug === article.category);
  const relatedArticles = articles
    .filter(a => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cherryvideo.com/' },
      { '@type': 'ListItem', position: 2, name: categoryInfo?.name || '', item: `https://cherryvideo.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://cherryvideo.com/article/${article.id}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '樱桃视频',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <main className="min-h-screen pt-20 pb-14">
        <article>
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap">
                <Link to="/" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap">{categoryInfo?.name}</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 line-clamp-1">{article.title}</span>
              </nav>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 次阅读
                </span>
                <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                  {categoryInfo?.name}
                </span>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover max-h-[400px]"
                title={article.title}
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full cursor-pointer hover:bg-secondary-100 transition-colors duration-200">视频</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full cursor-pointer hover:bg-secondary-100 transition-colors duration-200">资讯</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full cursor-pointer hover:bg-secondary-100 transition-colors duration-200">热门内容</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full cursor-pointer hover:bg-secondary-100 transition-colors duration-200">观看指南</span>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group flex flex-col bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={rel.title}
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-2">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500">{rel.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}